import { Injectable, Logger } from '@nestjs/common';
import { createRequire } from 'node:module';
import { pathToFileURL } from 'node:url';
import * as fs from 'node:fs';
import * as path from 'node:path';
import type { PluginInstance, PluginPackage } from '../interfaces';

export interface PluginManifest {
  name: string;
  version?: string;
  displayName?: string;
  form?: {
    refType?: 'config' | 'action';
    schema?: unknown;
  };
  [key: string]: unknown;
}

// 社区标准模式：tsup shims 会为 CJS 输出自动处理 import.meta.url
const require = createRequire(import.meta.url);

export class PluginNotFoundError extends Error {
  readonly pluginKey: string;

  constructor(pluginKey: string) {
    super(`Plugin not found: ${pluginKey}`);
    this.name = 'PluginNotFoundError';
    this.pluginKey = pluginKey;
  }
}

export class PluginLoadError extends Error {
  constructor(pluginKey: string, reason: string) {
    super(`Failed to load plugin ${pluginKey}: ${reason}`);
    this.name = 'PluginLoadError';
  }
}

@Injectable()
export class PluginLoaderService {
  private readonly logger = new Logger(PluginLoaderService.name);
  private readonly pluginInstances = new Map<string, PluginInstance>();
  /** 记录每个插件的加载版本（时间戳），用于 ESM 缓存绕过 */
  private readonly pluginVersions = new Map<string, number>();
  /** 缓存插件的 manifest.json */
  private readonly manifestCache = new Map<string, PluginManifest | null>();

  /**
   * 从入口路径向上查找包根目录（包含 package.json 的目录）
   */
  private findPackageRoot(entryPath: string): string | null {
    let dir = path.dirname(entryPath);
    while (dir !== path.dirname(dir)) {
      // 未到达根目录
      if (fs.existsSync(path.join(dir, 'package.json'))) {
        return dir;
      }
      dir = path.dirname(dir);
    }
    return null;
  }

  /**
   * 读取并缓存 manifest
   */
  getManifest(pluginKey: string): PluginManifest | null {
    // 检查缓存
    if (this.manifestCache.has(pluginKey)) {
      return this.manifestCache.get(pluginKey) ?? null;
    }

    try {
      // 1. 获取插件入口路径
      const entryPath = require.resolve(pluginKey);

      // 2. 从入口路径反推包根目录
      const packageRoot = this.findPackageRoot(entryPath);
      if (!packageRoot) {
        this.manifestCache.set(pluginKey, null);
        return null;
      }

      // 3. 读取 manifest.json
      const manifestPath = path.join(packageRoot, 'manifest.json');
      if (!fs.existsSync(manifestPath)) {
        this.manifestCache.set(pluginKey, null);
        return null;
      }

      const content = fs.readFileSync(manifestPath, 'utf-8');
      const manifest = JSON.parse(content) as PluginManifest;
      this.manifestCache.set(pluginKey, manifest);
      return manifest;
    } catch {
      // 读取失败时缓存 null，避免重复尝试
      this.manifestCache.set(pluginKey, null);
      return null;
    }
  }

  /**
   * 检查插件是否需要 config（form.refType === 'config'）
   */
  isConfigRequired(pluginKey: string): boolean {
    const manifest = this.getManifest(pluginKey);
    return manifest?.form?.refType === 'config';
  }

  async loadPlugin(pluginKey: string, config?: unknown): Promise<PluginInstance> {
    const isConfigRequired = this.isConfigRequired(pluginKey);

    // refType: "config" 的插件不缓存，每次重新创建
    if (isConfigRequired) {
      return this.createPluginInstance(pluginKey, config);
    }

    // refType: "action" 的插件使用缓存
    const cached = this.pluginInstances.get(pluginKey);
    if (cached) {
      this.logger.debug(`Using cached plugin instance: ${pluginKey}`);
      return cached;
    }

    const instance = await this.createPluginInstance(pluginKey);
    this.pluginInstances.set(pluginKey, instance);
    return instance;
  }

  /**
   * 创建插件实例（内部方法）
   */
  private async createPluginInstance(pluginKey: string, config?: unknown): Promise<PluginInstance> {
    this.logger.log(`Loading plugin: ${pluginKey}${config !== undefined ? ' (with config)' : ''}`);

    try {
      // 1. 将包名解析为实际文件路径
      const resolvedPath = require.resolve(pluginKey);

      // 2. 使用 file:// URL + 时间戳绕过 ESM 缓存
      const version = this.pluginVersions.get(pluginKey) ?? 0;
      const fileUrl = pathToFileURL(resolvedPath).href;
      const importPath = version > 0 ? `${fileUrl}?v=${version}` : fileUrl;

      // 3. 动态导入（支持 ESM 和 CJS 插件）
      const mod = await import(importPath);
      const pluginPackage = (mod.default ?? mod) as PluginPackage;

      if (typeof pluginPackage.create !== 'function') {
        throw new PluginLoadError(pluginKey, 'Plugin does not export create() function');
      }

      const instance = pluginPackage.create(config);

      this.logger.log(`Plugin loaded successfully: ${pluginKey}`);

      return instance;
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code === 'MODULE_NOT_FOUND') {
        throw new PluginNotFoundError(pluginKey);
      }
      throw new PluginLoadError(
        pluginKey,
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  isPluginInstalled(pluginKey: string): boolean {
    try {
      require.resolve(pluginKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 清除插件缓存
   * - 清除应用层 pluginInstances 缓存
   * - 清除 manifest 缓存
   * - 清除 Node.js CJS 模块缓存（require.cache）
   * - 更新版本号，下次 import 时绕过 ESM 缓存
   * @param pluginKey - 插件标识，不传则清除所有
   */
  clearCache(pluginKey?: string): void {
    if (pluginKey) {
      this.pluginInstances.delete(pluginKey);
      this.manifestCache.delete(pluginKey);
      this.clearNodeModuleCache(pluginKey);
      // 更新版本号，下次 import 时使用新的 URL 绕过 ESM 缓存
      this.pluginVersions.set(pluginKey, Date.now());
      this.logger.log(`Cleared cache for plugin: ${pluginKey}`);
    } else {
      // 清除所有插件
      for (const key of this.pluginInstances.keys()) {
        this.clearNodeModuleCache(key);
        this.pluginVersions.set(key, Date.now());
      }
      this.pluginInstances.clear();
      this.manifestCache.clear();
      this.logger.log('Cleared all plugin caches');
    }
  }

  /**
   * 清除 CJS 模块缓存
   */
  private clearNodeModuleCache(pluginKey: string): void {
    try {
      const resolvedPath = require.resolve(pluginKey);

      // 清除主模块及其依赖
      const mod = require.cache[resolvedPath];
      if (mod) {
        this.clearModuleAndChildren(mod, pluginKey);
      }

      delete require.cache[resolvedPath];
    } catch {
      // 插件可能未安装或是纯 ESM 模块，忽略错误
    }
  }

  /**
   * 递归清除子模块缓存
   */
  private clearModuleAndChildren(mod: NodeModule, pluginKey: string): void {
    // 只清除插件目录下的模块，避免清除核心模块
    const pluginScope = pluginKey.startsWith('@') ? pluginKey.split('/').slice(0, 2).join('/') : pluginKey;

    mod.children.forEach(child => {
      if (child.filename.includes(`node_modules/${pluginScope}`)) {
        this.clearModuleAndChildren(child, pluginKey);
        delete require.cache[child.filename];
      }
    });
  }

  /**
   * 强制重新加载插件
   * @param pluginKey - 插件标识
   */
  async reloadPlugin(pluginKey: string): Promise<PluginInstance> {
    this.clearCache(pluginKey);
    return this.loadPlugin(pluginKey);
  }
}
